// ============================================================================
// "I'm not sure — walk me through it." Rung 2 of Ben's ladder. 2026-09-03.
//
//   FORM   → the AM knows the words. Five divisions, a reason, done.
//   HELPER → this. Plain questions, one screen at a time, no vocabulary.
//   TEXT   → the last resort, and it lives INSIDE here: as the closing note when
//            something was answered "I'm not sure", or as the whole record on
//            "None of these", after six real answers have been rejected.
//
// ⛔ SECOND PASS, AND THE REASON FOR IT. Ben, 2026-09-03: *"It stops after a
// couple fields. Why were these removed? The demo had them. Asking them helps
// Jose with the classification. As it stands, it is not helpful… You took the
// three rung concept and made a mini (mostly useless) helper that still
// basically defaults to text."*
//
// He is right. The first pass asked the two questions the DATABASE needed — what
// happened, and which one — and stopped. The helper is not for the database. It
// is for the person who cannot classify a record but can still say where, when,
// who, and whether money moves; those four are what let Finance finish the job.
// A division with no building and no sub under it is a category, not a record.
//
// ⭐ "I'M NOT SURE" IS ON EVERY SCREEN AND BLOCKS NOTHING. It does two things
// and only two: it is named for Finance, and it makes the closing note REQUIRED.
// Ben: *"If 'I don't know' or 'it's complicated' are selected, THEN default to
// text."* Text is the fallback for a gap — never the first thing offered.
//
// ⛔ AND STILL ONE ROUTE TO Unclassified: "None of these". An AM who does not
// know the building but does know they covered a shift is filed as Coverage with
// the gap named. Sending that to Finance's queue would throw away the answer
// they were sure of.
//
// ⛔ ONE COMPONENT, TWO DOORS — the entry screen and a stuck card. The card door
// passes what the record already knows as `prefilled`; it is shown, not re-asked.
// ============================================================================
import { useEffect, useMemo, useState } from 'react';
import { X, Check } from 'lucide-react';
import {
  MONEY_DIRECTIONS,
  NONE_OF_THESE,
  UNSURE,
  WHAT_HAPPENED,
  blankHelperAnswers,
  helperFullResult,
  helperNote,
  type HelperAnswersFull,
  type MoneyDirection,
  type WhenKind,
} from '../lib/classify-helper';
import { SmartSelect } from '../../../components/smart-select';
import { isProviderSelectable } from '../lib/sub-picker';

const PILL = 'rounded-full border px-3 py-1.5 text-[12.5px] transition disabled:opacity-50';
const PILL_OFF = 'border-tcs-border bg-tcs-raised text-tcs-text hover:border-tcs-text-muted';
const PILL_ON = 'border-tcs-text bg-tcs-inverse text-tcs-inverse-ink';
const CHOICE = 'w-full rounded-lg border px-3 py-2.5 text-left text-sm transition';
const CHOICE_OFF = 'border-tcs-border bg-tcs-raised text-tcs-text hover:border-tcs-text-muted';
const CHOICE_ON = 'border-tcs-text bg-tcs-field text-tcs-text font-semibold';
const CHOICE_UNSURE = 'w-full rounded-lg border border-dashed px-3 py-2.5 text-left text-sm transition';
const CHOICE_UNSURE_OFF =
  'border-tcs-border bg-tcs-bg text-tcs-text-secondary hover:border-tcs-text-muted';
const INPUT =
  'block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text';

export interface HelperOutcome {
  /** null when the AM fell through to text. */
  division_id: string | null;
  reason_code: string | null;
  /** true when this must be saved as Unclassified for Finance to sort. */
  unclassified: boolean;
  /** The note for the record — the AM's words first, our summary appended. */
  words: string;
  /**
   * ⭐ WHAT THE FIRST PASS THREW AWAY. The helper collects real facts now, and
   * they belong on the record: the caller fills the form or patches the row.
   */
  facility_ids: string[];
  /**
   * The raw picker rows behind `facility_ids`, in the same order.
   *
   * ⛔ Carried so the entry screen can set its facility WITHOUT a second fetch of
   * a row the picker already had. Deliberately `Record<string, unknown>` — the
   * helper does not know the shape of a facility and must not learn it; the
   * caller reads the columns it needs.
   */
  facility_rows: Record<string, unknown>[];
  date_from: string;
  date_to: string;
  assigned_partner_id: string;
  money: MoneyDirection | null;
  /** Does this path still owe an amount? Drives whether the form asks for one. */
  needsClientAmount: boolean;
  needsSubAmount: boolean;
  /** Named, so a caller can show them without recomputing. */
  gaps: string[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onDone: (outcome: HelperOutcome) => void;
  /** What the record already knows. Shown as context; never re-asked. */
  prefilled?: { label: string; value: string }[];
  /** Any words already on the record, so re-opening does not lose them. */
  initialWords?: string;
  /** The record's own facts, so the helper starts where the AM already is. */
  initialFacilityIds?: string[];
  initialDate?: string;
  initialPartnerId?: string;
}

/** The screens, in order. `reason` appears only when something was classified. */
type ScreenKey = 'where' | 'when' | 'what' | 'reason' | 'who' | 'money' | 'notes';

export function ClassifyHelperDialog({
  open,
  onClose,
  onDone,
  prefilled = [],
  initialWords = '',
  initialFacilityIds = [],
  initialDate = '',
  initialPartnerId = '',
}: Props) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<HelperAnswersFull>(blankHelperAnswers());
  // Ids are what gets written; names are what the AM reads back. Kept apart so
  // the answer model never carries a display string it would have to keep fresh.
  const [names, setNames] = useState<Record<string, string>>({});
  const [rawRows, setRawRows] = useState<Record<string, Record<string, unknown>>>({});

  // Re-opening starts clean, but never throws away what the record already has.
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setNames({});
    setRawRows({});
    setAnswers({
      ...blankHelperAnswers(),
      words: initialWords,
      facility_ids: initialFacilityIds,
      date_from: initialDate,
      when: initialDate ? 'day' : null,
      assigned_partner_id: initialPartnerId,
    });
  }, [open, initialWords, initialFacilityIds, initialDate, initialPartnerId]);

  const chosen = useMemo(
    () => WHAT_HAPPENED.find((w) => w.key === answers.what) ?? null,
    [answers.what],
  );
  const fellThrough = answers.what === NONE_OF_THESE;
  const result = helperFullResult(answers);

  // ⛔ THE ORDER IS THE DESIGN. Where and when first — the two an AM always
  // knows, and they cost nothing. What-happened third, once they are warmed up.
  // The money question near the end, when the shape of the event is settled and
  // "no, the hours are logged" reads as an answer rather than an evasion.
  const screens: ScreenKey[] = useMemo(() => {
    const s: ScreenKey[] = ['where', 'when', 'what'];
    if (answers.what && !fellThrough) s.push('reason');
    s.push('who', 'money', 'notes');
    return s;
  }, [answers.what, fellThrough]);

  const at = screens[Math.min(step, screens.length - 1)];
  const isLast = step >= screens.length - 1;

  if (!open) return null;

  function set<K extends keyof HelperAnswersFull>(k: K, v: HelperAnswersFull[K]) {
    setAnswers((a) => ({ ...a, [k]: v }));
  }

  function pickWhat(key: string) {
    setAnswers((a) => ({ ...a, what: key, reason_code: null }));
    setStep((s) => s + 1);
  }

  function finish() {
    onDone({
      division_id: result.division_id,
      reason_code: result.reason_code,
      unclassified: result.event_class_hint === 'unclassified',
      words: helperNote(answers),
      facility_ids: answers.facility_ids,
      facility_rows: answers.facility_ids.map((id) => rawRows[id] ?? { facility_id: id }),
      date_from: answers.date_from,
      date_to: answers.when === 'range' ? answers.date_to : '',
      assigned_partner_id: answers.assigned_partner_id,
      money: answers.money,
      needsClientAmount: result.needsClientAmount,
      needsSubAmount: result.needsSubAmount,
      gaps: result.gaps,
    });
  }

  /** One "I'm not sure", so every screen offers the same escape in the same words. */
  function unsureButton(on: boolean, onPick: () => void, label?: string) {
    return (
      <button
        type="button"
        onClick={onPick}
        className={`${CHOICE_UNSURE} ${on ? CHOICE_ON : CHOICE_UNSURE_OFF}`}
      >
        {label ?? 'I’m not sure'}
        <span className="mt-0.5 block text-[11.5px] text-tcs-text-muted">
          Fine — we’ll ask you to say what you do know at the end.
        </span>
      </button>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-tcs-scrim/60 p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Walk me through it"
    >
      <div className="max-h-[92vh] w-full max-w-lg overflow-auto rounded-t-xl border border-tcs-border bg-tcs-bg p-4 shadow-lg sm:rounded-xl">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-tcs-text">Let&rsquo;s work out what this is</h2>
            <p className="mt-0.5 text-[11.5px] text-tcs-text-muted">
              Plain questions, one at a time. Nothing is required &mdash; &ldquo;I&rsquo;m not
              sure&rdquo; is always an answer.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-tcs-text-secondary hover:bg-tcs-surface hover:text-tcs-text"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Seven questions is a lot to walk into blind. A bar is the difference
            between "nearly there" and "how long is this going to be". */}
        <div className="mb-3 h-1 w-full overflow-hidden rounded-full bg-tcs-surface">
          <div
            className="h-full rounded-full bg-tcs-inverse transition-all"
            style={{ width: `${Math.round(((step + 1) / screens.length) * 100)}%` }}
          />
        </div>

        {/* What the record already knows. Shown, never re-asked. */}
        {prefilled.length > 0 && (
          <div className="mb-3 rounded-md border border-tcs-border bg-tcs-raised px-3 py-2">
            <p className="text-[10.5px] font-semibold uppercase tracking-wide text-tcs-text-muted">
              Already on this record
            </p>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
              {prefilled.map((p) => (
                <span key={p.label} className="text-[12px] text-tcs-text-secondary">
                  {p.label}: <span className="text-tcs-text">{p.value}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ---- 1 · WHERE. More than one, because an event can span buildings. */}
        {at === 'where' && (
          <div>
            <p className="mb-1 text-sm font-semibold text-tcs-text">Which building?</p>
            <p className="mb-3 text-[11.5px] text-tcs-text-muted">
              Where it happened. Add more than one if it spanned buildings.
            </p>
            <SmartSelect
              entity="facility"
              value={null}
              onChange={(id, row) => {
                if (!id) return;
                if (row?.name) setNames((n) => ({ ...n, [id]: row.name }));
                if (row?.raw) setRawRows((r) => ({ ...r, [id]: row.raw }));
                setAnswers((a) =>
                  a.facility_ids.includes(id)
                    ? a
                    : { ...a, facility_ids: [...a.facility_ids, id], buildingsUnsure: false },
                );
              }}
              entityLabel="building"
              placeholder="Search buildings…"
            />
            {answers.facility_ids.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {answers.facility_ids.map((id) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() =>
                      set('facility_ids', answers.facility_ids.filter((x) => x !== id))
                    }
                    className={`${PILL} ${PILL_ON}`}
                    aria-label={`Remove ${names[id] ?? id}`}
                  >
                    {names[id] ?? id} <span className="ml-1 opacity-70">&times;</span>
                  </button>
                ))}
              </div>
            )}
            <div className="mt-3">
              {unsureButton(answers.buildingsUnsure, () =>
                setAnswers((a) => ({ ...a, buildingsUnsure: !a.buildingsUnsure, facility_ids: [] })),
              )}
            </div>
          </div>
        )}

        {/* ---- 2 · WHEN. A stretch of days is still ONE event. */}
        {at === 'when' && (
          <div>
            <p className="mb-1 text-sm font-semibold text-tcs-text">Roughly when?</p>
            <p className="mb-3 text-[11.5px] text-tcs-text-muted">
              A day is enough, and it does not have to be exact &mdash; Finance needs a period to
              put it in, not a timestamp.
            </p>
            <div className="space-y-2">
              {(
                [
                  ['day', 'A single day'],
                  ['range', 'Over a stretch of days'],
                ] as [WhenKind, string][]
              ).map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => set('when', k)}
                  className={`${CHOICE} ${answers.when === k ? CHOICE_ON : CHOICE_OFF}`}
                >
                  {label}
                </button>
              ))}
              {unsureButton(answers.when === UNSURE, () => set('when', UNSURE))}
            </div>
            {answers.when === 'day' && (
              <input
                type="date"
                value={answers.date_from}
                onChange={(e) => set('date_from', e.target.value)}
                className={`${INPUT} mt-3`}
              />
            )}
            {answers.when === 'range' && (
              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-[11px] text-tcs-text-secondary">From</span>
                  <input
                    type="date"
                    value={answers.date_from}
                    onChange={(e) => set('date_from', e.target.value)}
                    className={INPUT}
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-[11px] text-tcs-text-secondary">To</span>
                  <input
                    type="date"
                    value={answers.date_to}
                    onChange={(e) => set('date_to', e.target.value)}
                    className={INPUT}
                  />
                </label>
              </div>
            )}
          </div>
        )}

        {/* ---- 3 · WHAT. This question IS the division picker, in plain English. */}
        {at === 'what' && (
          <div>
            <p className="mb-1 text-sm font-semibold text-tcs-text">What happened?</p>
            <p className="mb-3 text-[11.5px] text-tcs-text-muted">
              Closest match. Finance can change it later.
            </p>
            <div className="space-y-2">
              {WHAT_HAPPENED.map((w) => (
                <button
                  key={w.key}
                  type="button"
                  onClick={() => pickWhat(w.key)}
                  className={`${CHOICE} ${answers.what === w.key ? CHOICE_ON : CHOICE_OFF}`}
                >
                  {w.prompt}
                </button>
              ))}
              {/* ⛔ RUNG 3, and the only door to it. Last, dashed, quieter — the
                  AM has now read six real answers and rejected every one. */}
              <button
                type="button"
                onClick={() => pickWhat(NONE_OF_THESE)}
                className={`${CHOICE} border-dashed ${fellThrough ? CHOICE_ON : CHOICE_UNSURE_OFF}`}
              >
                None of these — let me describe it
                <span className="mt-0.5 block text-[11.5px] text-tcs-text-muted">
                  Finance classifies it instead of you.
                </span>
              </button>
            </div>
          </div>
        )}

        {/* ---- 4 · REASON. Skipped entirely on the fall-through. */}
        {at === 'reason' && chosen && (
          <div>
            <p className="mb-1 text-sm font-semibold text-tcs-text">Which was it?</p>
            <p className="mb-3 text-[11.5px] text-tcs-text-muted">&ldquo;{chosen.prompt}&rdquo;</p>
            <div className="flex flex-wrap gap-2">
              {chosen.followUp.map((f) => (
                <button
                  key={f.reason_code}
                  type="button"
                  onClick={() => set('reason_code', f.reason_code)}
                  className={`${PILL} ${answers.reason_code === f.reason_code ? PILL_ON : PILL_OFF}`}
                >
                  {f.prompt}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ---- 5 · WHO. Ben: "ASK about facility (or multiple facilities) and sub." */}
        {at === 'who' && (
          <div>
            <p className="mb-1 text-sm font-semibold text-tcs-text">Who did the work?</p>
            <p className="mb-3 text-[11.5px] text-tcs-text-muted">
              The sub or the crew. No amount needed here &mdash; Finance finds the hours and
              applies the rate.
            </p>
            <SmartSelect
              entity="service_provider"
              scope={answers.facility_ids[0] ? { facility_id: answers.facility_ids[0] } : undefined}
              value={answers.assigned_partner_id || null}
              onChange={(id, row) => {
                if (id && row?.name) setNames((n) => ({ ...n, [id]: row.name }));
                setAnswers((a) => ({ ...a, assigned_partner_id: id || '', subUnsure: false }));
              }}
              isSelectable={isProviderSelectable}
              entityLabel="sub"
              placeholder="Search subs…"
            />
            <div className="mt-3">
              {unsureButton(answers.subUnsure, () =>
                setAnswers((a) => ({ ...a, subUnsure: !a.subUnsure, assigned_partner_id: '' })),
              )}
            </div>
          </div>
        )}

        {/* ---- 6 · MONEY. This screen is AFB-0085 (3), which Ben asked for three
             times: may an AM hand a record to Finance with NO money answer? Yes. */}
        {at === 'money' && (
          <div>
            <p className="mb-1 text-sm font-semibold text-tcs-text">Does money need to move?</p>
            <p className="mb-3 text-[11.5px] text-tcs-text-muted">
              You do not have to know an amount to answer this.
            </p>
            <div className="space-y-2">
              {MONEY_DIRECTIONS.map((m) =>
                m.key === UNSURE ? (
                  <div key={m.key}>
                    {unsureButton(answers.money === UNSURE, () => set('money', UNSURE), m.prompt)}
                  </div>
                ) : (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => set('money', m.key as MoneyDirection)}
                    className={`${CHOICE} ${answers.money === m.key ? CHOICE_ON : CHOICE_OFF}`}
                  >
                    {m.prompt}
                  </button>
                ),
              )}
            </div>
            {(result.needsClientAmount || result.needsSubAmount) && (
              <p className="mt-3 rounded-md bg-tcs-raised px-3 py-2 text-[11.5px] text-tcs-text-secondary">
                You&rsquo;ll type the{' '}
                {result.needsClientAmount && result.needsSubAmount
                  ? 'amounts'
                  : result.needsClientAmount
                    ? 'client amount'
                    : 'sub amount'}{' '}
                on the form afterwards.
              </p>
            )}
          </div>
        )}

        {/* ---- 7 · NOTES. Where TEXT actually lives, and why it is last. */}
        {at === 'notes' && (
          <div>
            <p className="mb-1 text-sm font-semibold text-tcs-text">
              {fellThrough ? 'Describe it however makes sense' : 'Anything else Finance should know?'}
            </p>
            <p className="mb-3 text-[11.5px] text-tcs-text-muted">
              {fellThrough
                ? 'In your own words. Finance will read this and classify it.'
                : result.gaps.length > 0
                  ? `You weren’t sure about ${result.gaps.join(', ')}. Say what you do know.`
                  : 'Optional — you have already answered everything Finance needs.'}
            </p>
            <textarea
              value={answers.words}
              onChange={(e) => set('words', e.target.value)}
              rows={4}
              autoFocus
              placeholder="Esmeralda covered Curtis's Tuesday shift at Brookside ER."
              className={INPUT}
            />

            {/* Seven answers is enough that the AM should see what they said
                before it is written down. */}
            <div className="mt-3 rounded-md border border-tcs-border bg-tcs-raised px-3 py-2">
              <p className="text-[10.5px] font-semibold uppercase tracking-wide text-tcs-text-muted">
                What gets recorded
              </p>
              <ul className="mt-1 space-y-0.5 text-[12px] text-tcs-text-secondary">
                <li>
                  Building:{' '}
                  <span className="text-tcs-text">
                    {answers.buildingsUnsure
                      ? 'not sure'
                      : answers.facility_ids.length > 0
                        ? answers.facility_ids.map((id) => names[id] ?? id).join(', ')
                        : '—'}
                  </span>
                </li>
                <li>
                  When:{' '}
                  <span className="text-tcs-text">
                    {answers.when === UNSURE
                      ? 'not sure'
                      : answers.when === 'range'
                        ? `${answers.date_from || '?'} → ${answers.date_to || '?'}`
                        : answers.date_from || '—'}
                  </span>
                </li>
                <li>
                  Sub:{' '}
                  <span className="text-tcs-text">
                    {answers.subUnsure
                      ? 'not sure'
                      : answers.assigned_partner_id
                        ? (names[answers.assigned_partner_id] ?? answers.assigned_partner_id)
                        : '—'}
                  </span>
                </li>
                <li>
                  Money:{' '}
                  <span className="text-tcs-text">
                    {MONEY_DIRECTIONS.find((m) => m.key === answers.money)?.prompt ?? '—'}
                  </span>
                </li>
              </ul>
            </div>

            {result.blockers.length > 0 && (
              <p className="mt-2 rounded-md bg-st-warn-bg px-3 py-2 text-[11.5px] text-st-warn-fg">
                Still needs {result.blockers.join(' and ')}.
              </p>
            )}

            {/* Name the outcome. This is what makes the fall-through read as a
                last resort rather than a shortcut. */}
            {result.done && (
              <p
                className={`mt-2 flex items-start gap-1.5 rounded-md px-3 py-2 text-[11.5px] ${
                  fellThrough ? 'bg-st-warn-bg text-st-warn-fg' : 'bg-st-ok-bg text-st-ok-fg'
                }`}
              >
                <Check className="mt-[1px] h-3.5 w-3.5 shrink-0" />
                {fellThrough
                  ? 'This goes to Finance to classify — with everything else you answered.'
                  : 'That is all we needed. Finance will not have to sort this one.'}
              </p>
            )}
          </div>
        )}

        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => (step === 0 ? onClose() : setStep((s) => s - 1))}
            className="rounded-md border border-tcs-border bg-tcs-bg px-3 py-2 text-sm text-tcs-text hover:bg-tcs-surface"
          >
            {step === 0 ? 'Cancel' : 'Back'}
          </button>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-tcs-text-muted">
              {step + 1} of {screens.length}
            </span>
            <button
              type="button"
              disabled={isLast && !result.done}
              onClick={() => (isLast ? finish() : setStep((s) => s + 1))}
              className="rounded-md border border-tcs-text bg-tcs-inverse px-4 py-2 text-sm font-semibold text-tcs-inverse-ink disabled:cursor-not-allowed disabled:opacity-40"
            >
              {!isLast ? 'Next' : fellThrough ? 'Send to Finance' : 'Use this'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
